/**
 * API client. The access token lives in memory only (a stored token is
 * readable by any script of the page); continuity across page loads comes
 * from the httpOnly refresh cookie. Every request, download and upload goes
 * through one path: a 401 triggers one shared refresh, then one retry, and a
 * failed refresh ends the session instead of leaving a stale one.
 */
export interface User {
  id: string
  orgId: number
  username: string
  fullName: string
  extension: string | null
  role: string
  permissions: string[]
  /** Held to their own team of agents (migration 159, the role's « limité à son équipe »). */
  teamOnly?: boolean
}

/** The org profile the API sends with a session (login, refresh, /auth/me). */
export interface Org {
  id: number
  slug: string
  name: string
  country: string
  timezone: string
  currency: string
  locale: 'fr' | 'ar' | 'en'
  /** Modules switched on for the org (API core/modules.ts); absent: all. */
  modules?: string[]
  /** Address of the org's logo (versioned), or null. */
  logoUrl?: string | null
  /** The zone's UTC offset now, from the server's tz data. */
  utcOffsetMinutes?: number
  /** The zone's offsets over five years: the screens' clock (lib/orgClock.ts). */
  utcOffsets?: Array<{ from: string; offset: number }>
}

export interface Session { accessToken: string; user: User; org: Org }

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string,
              /** What the API adds to its refusal (the row in conflict…). */
              readonly details?: unknown) {
    super(message)
  }
  /** A missing permission, as opposed to a failure: screens say it differently. */
  get forbidden(): boolean { return this.status === 403 }
}

let accessToken: string | null = null
export const setAccessToken = (t: string | null): void => { accessToken = t }

/*
 * A support session (opened from the platform console) has no refresh cookie
 * of its own: when its token expires it ends. Renewing it would pick up
 * whatever other org session this browser holds, and switch accounts
 * without a word.
 */
let refreshAllowed = true
export const disallowRefresh = (): void => { refreshAllowed = false }
export const getAccessToken = (): string | null => accessToken

let sessionLost: (() => void) | null = null
/** Called once the session cannot be renewed (refresh refused). */
export function onSessionLost(fn: () => void): void { sessionLost = fn }

let sessionRenewed: ((s: Session) => boolean) | null = null
/**
 * Sees every renewed session before it is applied. Returning false refuses
 * it: a refresh that answers for another account (a login in another tab)
 * must never be applied silently under the current one.
 */
export function onSessionRenewed(fn: (s: Session) => boolean): void { sessionRenewed = fn }

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
export interface RequestOptions {
  method?: Method
  body?: unknown
  signal?: AbortSignal
}

function send(path: string, o: RequestOptions): Promise<Response> {
  const form = o.body instanceof FormData
  return fetch(path, {
    method: o.method ?? 'GET',
    credentials: 'same-origin',
    signal: o.signal,
    headers: {
      ...(o.body === undefined || form ? {} : { 'Content-Type': 'application/json' }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(o.body === undefined ? {} : { body: form ? (o.body as FormData) : JSON.stringify(o.body) }),
  })
}

/**
 * One refresh at a time: ten requests expiring together must not rotate the
 * token ten times. Within a tab the promise is shared; across the tabs of the
 * origin a Web Lock serialises them, so the next tab sends the cookie the
 * previous one just rotated instead of replaying the old one (which the
 * server treats as theft and answers by revoking the whole session family).
 */
let refreshing: Promise<Session | null> | null = null
export const REFRESH_LOCK = 'tsh-auth-refresh'

/**
 * The refresh could not be answered (network down, a 5xx): not a refusal.
 * The session is kept, and the request that needed it fails with this error.
 */
export class RefreshUnavailable extends Error {}

async function refreshOnce(): Promise<Session | null> {
  if (!refreshAllowed) return null
  let r: Response
  try {
    r = await fetch('/auth/refresh', { method: 'POST', credentials: 'same-origin' })
  } catch {
    throw new RefreshUnavailable('network')
  }
  // Only a refusal ends the session; a failure of the server keeps it.
  if (r.status === 401 || r.status === 403) return null
  if (!r.ok) throw new RefreshUnavailable(`HTTP ${r.status}`)
  const s = (await r.json()) as Session
  if (sessionRenewed && !sessionRenewed(s)) return null
  setAccessToken(s.accessToken)
  return s
}

function withRefreshLock(run: () => Promise<Session | null>): Promise<Session | null> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  // The lock's callback returns a promise: request() resolves with its value.
  return locks?.request ? locks.request(REFRESH_LOCK, run).then((s) => s) : run()
}

/**
 * Renews the session from the refresh cookie. Resolves with the session, or
 * null when the server refuses it; rejects with RefreshUnavailable when the
 * server could not answer.
 */
export function refreshSession(): Promise<Session | null> {
  refreshing ??= withRefreshLock(refreshOnce)
    .finally(() => { refreshing = null })
  return refreshing
}

async function authorised(path: string, o: RequestOptions): Promise<Response> {
  const res = await send(path, o)
  if (res.status !== 401 || accessToken === null) return res
  let renewed: Session | null
  try {
    renewed = await refreshSession()
  } catch {
    // The server is unreachable or failing: the session stays, this request fails.
    throw new ApiError(503, 'SERVICE_UNAVAILABLE', '')
  }
  if (renewed) return send(path, o)
  setAccessToken(null)
  sessionLost?.()
  return res
}

/** Codes of errors the client made up because the server said nothing usable. */
export const SYNTHETIC_CODES = new Set(['HTTP_ERROR', 'INVALID_RESPONSE'])

async function errorOf(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '')
  let e: { code?: string; message?: string; details?: unknown } | undefined
  try { e = (JSON.parse(text) as { error?: typeof e }).error } catch { /* not JSON */ }
  // A code without a message keeps an empty message: the screen translates the code.
  if (e?.code) return new ApiError(res.status, e.code, e.message ?? '', e.details)
  return new ApiError(res.status, 'HTTP_ERROR', `HTTP ${res.status}`)
}

export async function api<T = unknown>(path: string, o: RequestOptions = {}): Promise<T> {
  const res = await authorised(path, o)
  if (!res.ok) throw await errorOf(res)
  const text = await res.text()
  if (!text) return null as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(res.status, 'INVALID_RESPONSE', `HTTP ${res.status}`)
  }
}

/**
 * Downloads a file served by the API. Not through `location.href`: that
 * navigation would carry no token (it lives in memory), and in development the
 * proxy answers every navigation with the SPA page.
 */
export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await authorised(path, {})
  if (!res.ok) throw await errorOf(res)
  await saveResponse(res, fallbackName)
}

/**
 * Downloads the file a POST answers with (an export built from a body of
 * choices). Returns the row count the server announces (x-row-count).
 */
export async function downloadPost(path: string, body: unknown, fallbackName: string): Promise<{ rows: number }> {
  const res = await authorised(path, { method: 'POST', body })
  if (!res.ok) throw await errorOf(res)
  await saveResponse(res, fallbackName)
  return { rows: Number(res.headers.get('x-row-count') ?? 0) }
}

/** Fetches a protected file as a blob (a recording to play, a logo preview). */
export async function fetchBlob(path: string): Promise<Blob> {
  const res = await authorised(path, {})
  if (!res.ok) throw await errorOf(res)
  return res.blob()
}

async function saveResponse(res: Response, fallbackName: string): Promise<void> {
  // The server names the file; the default only applies when it did not.
  const disposition = res.headers.get('content-disposition') ?? ''
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * URL of a server-sent event stream. EventSource cannot send a header, so
 * the address carries a stream ticket: valid a minute, for that stream only
 * (review R18), never the session token. A reopened stream asks for a new one.
 */
export async function streamUrl(path: string): Promise<string> {
  const { ticket } = await api<{ ticket: string }>('/auth/stream-ticket', { method: 'POST', body: { path } })
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}ticket=${encodeURIComponent(ticket)}`
}

/** The older form, the session token in the address: accepted outside production only. */
export function eventStreamUrl(path: string): string {
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}token=${encodeURIComponent(accessToken ?? '')}`
}
